export type AzureTranslationEntry = {
  name: string;
  nativeName: string;
  dir?: string;
};

export type StyleLanguageOption = {
  matchKey: string;
  /** English display name — used for A–Z sort / search. */
  name: string;
  nativeName: string;
  label: string;
};

const ZH_HANS_ALIASES = new Set([
  'zh-hans',
  'zh-cn',
  'zh-sg',
  'zh-hans-cn',
  'zh-hans-sg',
]);

const ZH_HANT_ALIASES = new Set([
  'zh-hant',
  'zh-tw',
  'zh-hk',
  'zh-mo',
  'zh-hant-tw',
  'zh-hant-hk',
  'zh-hant-mo',
]);

/**
 * Normalize a subtitle / catalog language code to a Style Studio match key.
 * - Default: primary subtag only (`en-US` → `en`).
 * - Chinese: keep Hans / Hant separate; bare `zh` returns '' (no auto-match).
 */
export function toStyleLanguageMatchKey(code: string): string {
  const raw = String(code || '').trim().toLowerCase().replace(/_/g, '-');
  if (!raw || raw === 'null' || raw === 'custom') return '';

  if (raw === 'zh') return '';
  if (ZH_HANS_ALIASES.has(raw) || raw.startsWith('zh-hans')) return 'zh-Hans';
  if (ZH_HANT_ALIASES.has(raw) || raw.startsWith('zh-hant')) return 'zh-Hant';
  // zh-CN style without explicit script already covered; other zh-* → no bare collapse
  if (raw.startsWith('zh-')) {
    const rest = raw.slice(3);
    if (rest === 'cn' || rest === 'sg') return 'zh-Hans';
    if (rest === 'tw' || rest === 'hk' || rest === 'mo') return 'zh-Hant';
    return '';
  }

  const primary = raw.split('-')[0] || '';
  return primary;
}

/** @deprecated Prefer buildStyleStudioLanguageOptions from styleStudioLanguageCatalog. */
export function buildStyleLanguageOptions(
  azureTranslation: Record<string, AzureTranslationEntry>,
): StyleLanguageOption[] {
  const out: StyleLanguageOption[] = [];
  const seen = new Set<string>();

  for (const code of Object.keys(azureTranslation)) {
    const entry = azureTranslation[code];
    if (!entry) continue;
    const matchKey = toStyleLanguageMatchKey(code);
    if (!matchKey || seen.has(matchKey)) continue;
    seen.add(matchKey);
    const name = entry.name || code;
    const native = entry.nativeName || name;
    out.push({
      matchKey,
      name,
      nativeName: native,
      label: `${name} (${native})`,
    });
  }

  return out;
}

export function normalizeStyleLanguageList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    // Already-canonical keys from Studio UI.
    const matchKey = item === 'zh-Hans' || item === 'zh-Hant'
      ? item
      : toStyleLanguageMatchKey(item);
    if (!matchKey || seen.has(matchKey)) continue;
    seen.add(matchKey);
    out.push(matchKey);
  }
  return out;
}
